import { Box, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { PAGE_SIZE } from 'services/constants/env';
import {
	getVatCorrectionsRequest,
	vatCorrectionDownloadRequest,
} from 'redux/reducers/vatCorrections/reducer';
import {
	getVatCorrections,
	getVatCorrectionsDownloadLoader,
	getVatCorrectionsLoader,
} from 'redux/reducers/vatCorrections/selectors';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { IPageResponse } from 'services/constants/types';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getFiltersFromNavigateParams, getOrderByFromNavigateParams } from 'utils/FilterParams';
import { CreateCorrectionButton } from './CreateCorrectionButton';
import { TVatCorrectionsTableItem } from './types';
import { VAT_CORRECTIONS_ACTIONS, vatCorrectionsTableStructure } from './vat-corrections-structure';

const DEFAULT_ORDER_BY_FIELD = 'timestamp';

export const VatCorrections: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = location;
	const navigateParams = queryString.parse(search);
	const { page } = navigateParams;
	const tableStructure = vatCorrectionsTableStructure as TTableStructure<
		keyof TVatCorrectionsTableItem
	>[];

	const vatCorrections = useSelector(getVatCorrections);
	const vatCorrectionsLoader = useSelector(getVatCorrectionsLoader);
	const downloadLoader = useSelector(getVatCorrectionsDownloadLoader);

	const [prevSearch, setPrevSearch] = useState(location.search);

	const tableItems = useMemo((): IPageResponse<TVatCorrectionsTableItem> | null => {
		if (!vatCorrections) return null;

		return {
			data: vatCorrections.data ?? [],
			totalCount: vatCorrections.totalCount ?? 0,
		};
	}, [vatCorrections]);

	const dispatchGetVatCorrections = () => {
		dispatch(
			getVatCorrectionsRequest({
				filters: getFiltersFromNavigateParams(tableStructure, navigateParams),
				orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams, {
					defaultFieldName: DEFAULT_ORDER_BY_FIELD,
				}),
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
			}),
		);
	};

	useEffect(() => {
		const prevParams = queryString.parse(prevSearch);
		const otherPrevParams = Object.entries(prevParams).filter(([key]) => key !== 'search');
		const otherPrevParamsString = otherPrevParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');
		const currentParams = queryString.parse(location.search);
		const otherCurrentParams = Object.entries(currentParams).filter(([key]) => key !== 'search');
		const otherCurrentParamsString = otherCurrentParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');

		if (
			prevParams.search !== currentParams.search ||
			((prevParams.search as string)?.length ?? 0) === 0 ||
			otherPrevParamsString !== otherCurrentParamsString
		) {
			dispatchGetVatCorrections();
		}
		setPrevSearch(location.search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const handleLink = (id: number) => {
		const item = tableItems?.data?.find((row) => row.id === id);
		if (!item?.orderId) return;

		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`/order/${item.orderId}`);
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		if (typeOfDialog !== VAT_CORRECTIONS_ACTIONS.DOWNLOAD || downloadLoader) return;

		const item = tableItems?.data?.find((row) => row.id === id);
		if (!item?.filename) return;

		dispatch(
			vatCorrectionDownloadRequest({
				id: String(item.id),
				filename: item.filename,
			}),
		);
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TVatCorrectionsTableItem>
				tableStructure={tableStructure}
				items={tableItems}
				itemsIsLoading={vatCorrectionsLoader}
				handleAction={handleAction}
				handleLink={handleLink}
				minWidth={1200}
				name="correction"
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						p: '2px 4px',
					}}
				>
					<Typography
						variant="h1"
						color="primary"
						sx={{
							fontSize: '1.1rem',
							fontWeight: 'bold',
							textAlign: 'center',
							ml: 1,
						}}
					>
						VAT Corrections
					</Typography>
					<CreateCorrectionButton />
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
